
import { useRef, useState } from 'react';
import { NoteComment } from '@/types/note';

export interface TextHistoryItem {
  content: string;
  timestamp: Date;
}

export const useNoteState = () => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const contentRef = useRef("");
  const lastHistoryAtRef = useRef(0);
  const restoreGuardRef = useRef<{ staleContent: string; restoredContent: string; until: number } | null>(null);
  const [color, setColor] = useState('#F2FCE2'); // Always use light green as default
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [comments, setComments] = useState<NoteComment[]>([]);
  const [selectedText, setSelectedText] = useState<{start: number; end: number; text: string} | null>(null);
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [textHistory, setTextHistory] = useState<TextHistoryItem[]>([]);
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);
  const [restoreRevision, setRestoreRevision] = useState(0);

  const addHistorySnapshot = (snapshot: string, force = false) => {
    if (!snapshot || !snapshot.trim()) return;

    const now = Date.now();
    if (!force && now - lastHistoryAtRef.current < 3000) return;

    lastHistoryAtRef.current = now;
    setTextHistory(history => {
      if (history[0]?.content === snapshot) return history;
      return [{ content: snapshot, timestamp: new Date() }, ...history].slice(0, 10);
    });
  };

  const updateContent = (newContent: string) => {
    const restoreGuard = restoreGuardRef.current;
    if (restoreGuard) {
      if (Date.now() < restoreGuard.until && newContent !== restoreGuard.restoredContent) {
        return;
      }
      if (Date.now() >= restoreGuard.until || newContent === restoreGuard.restoredContent) {
        restoreGuardRef.current = null;
      }
    }

    const previousContent = contentRef.current;
    if (previousContent !== newContent) {
      const isLargeChange = Math.abs(previousContent.length - newContent.length) > 40;
      addHistorySnapshot(previousContent, isLargeChange);
    }

    contentRef.current = newContent;
    setContent(newContent);
  };

  const restoreContent = (restoredContent: string) => {
    const currentContent = contentRef.current;
    if (currentContent === restoredContent) return;

    restoreGuardRef.current = {
      staleContent: currentContent,
      restoredContent,
      until: Date.now() + 750,
    };
    addHistorySnapshot(currentContent, true);
    contentRef.current = restoredContent;
    setContent(restoredContent);
    setRestoreRevision(revision => revision + 1);

    setTextHistory(history => history.filter(item => item.content !== restoredContent));
  };

  const resetContent = (newContent: string) => {
    contentRef.current = newContent;
    setContent(newContent);
    setTextHistory([]);
    lastHistoryAtRef.current = 0;
    setRestoreRevision(revision => revision + 1);
  };

  return {
    title,
    setTitle,
    content,
    setContent: updateContent,
    restoreContent,
    resetContent,
    color,
    setColor,
    showLinkDialog,
    setShowLinkDialog,
    linkUrl,
    setLinkUrl,
    comments,
    setComments,
    selectedText,
    setSelectedText,
    showCommentDialog,
    setShowCommentDialog,
    coverImage,
    setCoverImage,
    selectedGroup,
    setSelectedGroup,
    textHistory,
    restoreRevision,
    showRecoveryDialog,
    setShowRecoveryDialog,
  };
};
